import Vue from 'vue';
import BigNumber from 'bignumber.js';
import { addNotification, clearNotification } from '@/utils/notification';

export const state = {
  tokenId: 1,
  paymentTokenName: '',
  allowance: 0,
  price: 0,
  supply: null,
  limit: 0,
  userTokensCount: 0,
  userTokensTotal: 0,
  userCardLimit: 0,
  closedAfter: null,
  activeAfter: null,
};

export const getters = {
  allowance: (s) => s.allowance,
  price: (s) => s.price,
  supply: (s) => s.supply,
  tokenId: (s) => s.tokenId,
  paymentTokenName: (s) => s.paymentTokenName,
  limit: (s) => s.limit,
  userTokensCount: (s) => s.userTokensCount,
  userTokensTotal: (s) => s.userTokensTotal,
  userCardLimit: (s) => s.userCardLimit,
  closedAfter: (s) => s.closedAfter,
  activeAfter: (s) => s.activeAfter,
};

export const mutations = {
  setAllowance: (state, payload) => {
    state.allowance = payload;
  },
  setPrice: (state, payload) => {
    state.price = payload;
  },
  setSupply: (state, payload) => {
    state.supply = payload;
  },
  setLimit: (state, payload) => {
    state.limit = payload;
  },
  setClosedAfter: (state, payload) => {
    state.closedAfter = payload;
  },
  setActiveAfter: (state, payload) => {
    state.activeAfter = payload;
  },
  setUserTokensCount: (state, payload) => {
    state.userTokensCount = payload;
  },
  setUserTokensTotal: (state, payload) => {
    state.userTokensTotal = payload;
  },
  setCardLimit: (state, payload) => {
    state.userCardLimit = payload;
  },
  setPaymentTokenName: (state, payload) => {
    state.paymentTokenName = payload;
  },
  setTokenId: (state, payload) => {
    state.tokenId = payload;
  },
};

export const actions = {
  async allowanceToken({ rootState, commit, state }) {
    try {
      const tokenAllowance = Vue.$contracts.getContract(state.paymentTokenName).methods.allowance;
      const launchpadPreSaleAddress = Vue.$contracts.getAddress('launchpadPreSale');

      const allowance = await tokenAllowance(
        rootState.User.ethAddress,
        launchpadPreSaleAddress,
      ).call();
      commit('setAllowance', Number(allowance));
    } catch (e) {
      console.log(e);
    }
  },

  async approveToken({ rootState, state }, amount) {
    try {
      const launchpadPreSaleAddress = Vue.$contracts.getAddress('launchpadPreSale');
      const tokenApprove = Vue.$contracts.getContract(state.paymentTokenName).methods.approve;
      await tokenApprove(launchpadPreSaleAddress, Vue.$BN(amount).toString())
        .send({
          from: rootState.User.ethAddress,
        })
        .once('transactionHash', (hash) => {
          addNotification(
            {
              type: 'info',
              title: 'Pending approve',
              data: { hash },
            },
            { root: true },
          );
        })
        .once('confirmation', () => {
          clearNotification('info');
          addNotification(
            {
              type: 'success',
              title: 'Successfully approved!',
              config: { duration: 15000 },
            },
            { root: true },
          );
        })
        .once('error', () => {
          clearNotification('info');
          addNotification(
            {
              type: 'warn',
              title: 'Approve failed',
              config: { duration: 5000 },
            },
            { root: true },
          );
        });
    } catch (e) {
      console.log(e);
    }
  },

  async registerForPreSale({ rootState, state, dispatch }, quantity) {
    const register = Vue.$contracts.getContract('launchpadPreSale').methods.registerForPreSale;
    const tokenAddress = Vue.$contracts.getAddress(state.paymentTokenName);
    await register(tokenAddress, [[state.tokenId, quantity]])
      .send({
        from: rootState.User.ethAddress,
      })
      .once('transactionHash', (hash) => {
        addNotification(
          {
            type: 'info',
            title: 'Pending register sale:',
            data: { hash },
          },
          { root: true },
        );
      })
      .once('confirmation', () => {
        clearNotification('info');
        addNotification(
          {
            type: 'success',
            title: 'Successfully registered!',
            config: { duration: 15000 },
          },
          { root: true },
        );
        dispatch('getPresaleInfo', {
          tokenName: state.paymentTokenName,
          tokenId: state.tokenId,
        });
      })
      .once('error', () => {
        clearNotification('info');
        addNotification(
          {
            type: 'warn',
            title: 'Register sale failed',
            config: { duration: 5000 },
          },
          { root: true },
        );
      });
  },

  async presaleDate({ commit }) {
    try {
      const presale = Vue.$contracts.getContract('launchpadPreSale').methods;
      const [active, closed] = await Promise.all([
        presale.activeAfter().call(),
        presale.closedAfter().call(),
      ]);
      commit('setClosedAfter', Number(closed));
      commit('setActiveAfter', Number(active));
    } catch (e) {
      console.log(e);
    }
  },

  async maxTotalSupply({ commit, state }) {
    try {
      const presale = Vue.$contracts.getContract('launchpadPreSale').methods;
      const res = await presale.maxTotalSupply(state.tokenId).call();

      commit('setSupply', {
        maxTotalSupply: Number(res.maxTotalSupply),
        currentSupply: Number(res.currentSupply),
      });
    } catch (e) {
      console.log(e);
    }
  },

  async getUserLimits({ state, rootState, commit }) {
    const presale = Vue.$contracts.getContract('launchpadPreSale').methods;
    const userTokensCount = await presale
      .getUserLimitForCard(rootState.User.ethAddress, state.tokenId)
      .call();
    commit('setUserTokensCount', Number(userTokensCount));
  },

  // shows how much tokens each user can buy
  async getCardLimit({ state, commit }) {
    const presale = Vue.$contracts.getContract('launchpadPreSale').methods;
    const userCardLimit = await presale
      .UserLimitCardId(state.tokenId)
      .call();
    commit('setCardLimit', Number(userCardLimit));
  },

  // compare limits to CARDS_PER_USER
  async getUserLimitsTotal({ rootState, commit }) {
    const presale = Vue.$contracts.getContract('launchpadPreSale').methods;
    const userTokensTotal = await presale
      .getUserLimitTotal(rootState.User.ethAddress)
      .call();
    commit('setUserTokensTotal', Number(userTokensTotal));
  },

  async getPresaleInfo({ state, rootState, commit }, { tokenName, tokenId }) {
    commit('setPaymentTokenName', String(tokenName));
    commit('setTokenId', Number(tokenId));

    const tokenAddress = Vue.$contracts.getAddress(state.paymentTokenName);
    const presale = Vue.$contracts.getContract('launchpadPreSale').methods;

    const [price, limit, userTokensCount] = await Promise.all([
      presale.getCardPrice(tokenAddress, state.tokenId).call(),
      presale.CARDS_PER_USER().call(),
      presale.getUserLimitForCard(rootState.User.ethAddress, state.tokenId).call(),
      this.dispatch('Presale/maxTotalSupply'),
      this.dispatch('Presale/allowanceToken'),
      this.dispatch('Presale/presaleDate'),
      this.dispatch('Presale/getCardLimit'),
      this.dispatch('Presale/getUserLimitsTotal'),
    ]);

    commit('setUserTokensCount', Number(userTokensCount));
    commit('setLimit', Number(limit));
    commit('setPrice', new BigNumber(price.value));
  },
};
